import { all, takeLatest, takeEvery, put, call, race, select, take } from 'redux-saga/effects';
import { delay } from 'redux-saga';
import { sendRequest } from 'src/helpers/saga';
import { logger } from 'src/helpers/logger';
import { t } from 'src/i18n';
import { RootState } from 'src/redux/store';
import notifications from 'src/redux/notifications/actions';
import { exportLines, loadCard, loadLineHeaders, loadLines } from '../api';
import { requestCardDelete } from '../rateCardsShared/deleteCardRequest';
import { triggerDownload } from '../download';
import { buildLinesSearch, linesRequestKey } from '../searchParams';
import { hasPendingUpload } from '../phase';
import {
  RATE_CARD_HTTP,
  RATE_CARD_POLL_INTERVAL_MS,
  RATE_CARD_POLL_MAX_ATTEMPTS,
  RATE_CARD_POLL_MAX_FAILURES,
  RATE_CARD_SIDE_IDS,
} from '../constants';
import actions from './actions';
import actionTypes from './actionTypes';
import type {
  DeleteRequestAction,
  ExportLinesRequestAction,
  LoadCardRequestAction,
  LoadLineCountRequestAction,
  LoadLineHeadersRequestAction,
  LoadLinesRequestAction,
  PollStartAction,
} from './actions.d';

export function* loadCardRequest({ payload }: LoadCardRequestAction): Generator<any, void, any> {
  try {
    // `silent` is the polled read: a blip mid-import is not worth a toast every five seconds. The
    // store still records the error, which is what stops the loop after a run of them.
    const errorType = payload.silent ? undefined : notifications.REQUEST_ERROR;
    const result = yield call(sendRequest, loadCard, { id: payload.id }, errorType);

    if (result?.status === RATE_CARD_HTTP.NOT_FOUND) {
      yield put(actions.loadCardNotFound());

      return;
    }

    if (result?.status === 200 && result.data?.success?.data) {
      yield put(actions.loadCardSuccess(result.data.success.data));

      return;
    }

    yield put(actions.loadCardError());
  } catch (error) {
    logger.error(error);
    yield put(actions.loadCardError());
  }
}

/**
 * Re-reads the card until the importer is done: parsing is asynchronous and there is no push channel.
 * Two bounds — a run of failures gives up, and an abandoned `processing` stops after ~fifteen minutes.
 */
function* pollLoop(id: string): Generator<any, { abandoned: boolean }, any> {
  let failures = 0;

  for (let attempt = 0; attempt < RATE_CARD_POLL_MAX_ATTEMPTS; attempt += 1) {
    yield call(delay, RATE_CARD_POLL_INTERVAL_MS);
    yield call(loadCardRequest, { type: actionTypes.LOAD_CARD_REQUEST, payload: { id, silent: true } });

    const { card, error } = yield select((state: RootState) => state.RateCardView);

    if (error) {
      failures += 1;

      if (failures >= RATE_CARD_POLL_MAX_FAILURES) return { abandoned: true };
    } else {
      failures = 0;

      if (!hasPendingUpload(card)) return { abandoned: false };
    }
  }

  return { abandoned: true };
}

/**
 * `takeLatest` restarts the loop rather than stacking a second; the race is the escape hatch. Giving
 * up is announced, or the in-progress banner goes on promising status nobody is collecting.
 */
export function* pollCardRequest({ payload }: PollStartAction): Generator<any, void, any> {
  const { poll } = yield race({
    poll: call(pollLoop, payload.id),
    stopped: take([actionTypes.POLL_STOP, actionTypes.CLEAR_STORE]),
  });

  if (poll?.abandoned) yield put(actions.pollAbandoned());
}

/**
 * Lines are only ever written by a publish, so a non-zero count is the FE's published test. Both
 * sides are asked: the count is side-scoped, and a bill-only publish would otherwise read as none.
 */
export function* loadLineCountRequest({ payload }: LoadLineCountRequestAction): Generator<any, void, any> {
  try {
    // One question, so one report: only the first leg carries an `errorType`. `sendRequest` raises
    // its own toast for a 401 or 403 whatever is passed, so toasting from both would treble them.
    const results = yield all(
      RATE_CARD_SIDE_IDS.map((side, index) =>
        call(
          sendRequest,
          loadLines,
          { id: payload.id, side, search: { page: 1, pageSize: 1 } },
          index === 0 ? notifications.REQUEST_ERROR : undefined,
        ),
      ),
    );

    const counts = results.map((result: any) =>
      result?.status === 200 ? result.data?.success?.data?.totalCount : undefined,
    );

    if (counts.every((count: unknown) => typeof count === 'number')) {
      yield put(actions.loadLineCountSuccess({ total: counts.reduce((sum: number, count: number) => sum + count, 0) }));

      return;
    }

    // Reachable from a 200 whose envelope drifted, where nothing below has logged anything. The page
    // then tells the user to refresh, which will never help, so the cause has to reach the logs here.
    logger.error(new Error(`rate card ${payload.id}: line count probe returned no usable totalCount`));
    yield put(actions.loadLineCountError());
  } catch (error) {
    logger.error(error);
    yield put(actions.loadLineCountError());
  }
}

export function* loadLineHeadersRequest({ payload }: LoadLineHeadersRequestAction): Generator<any, void, any> {
  try {
    const result = yield call(
      sendRequest,
      loadLineHeaders,
      { id: payload.id, side: payload.side },
      notifications.REQUEST_ERROR,
    );

    if (result?.status === 200 && result.data?.success?.data?.headers) {
      yield put(actions.loadLineHeadersSuccess({ headers: result.data.success.data.headers }));

      return;
    }

    yield put(actions.loadLineHeadersError());
  } catch (error) {
    logger.error(error);
    yield put(actions.loadLineHeadersError());
  }
}

export function* loadLinesRequest({ payload }: LoadLinesRequestAction): Generator<any, void, any> {
  try {
    const result = yield call(
      sendRequest,
      loadLines,
      { id: payload.id, side: payload.side, search: buildLinesSearch(payload) },
      notifications.REQUEST_ERROR,
    );

    if (result?.status === 200 && result.data?.success?.data?.records) {
      const { records, filteredCount, sortableColumns = [], filters = [] } = result.data.success.data;

      yield put(
        actions.loadLinesSuccess({
          records,
          filteredTotal: filteredCount,
          filters,
          sortableColumns,
          side: payload.side,
          requestKey: linesRequestKey(payload),
        }),
      );

      return;
    }

    yield put(actions.loadLinesError());
  } catch (error) {
    logger.error(error);
    yield put(actions.loadLinesError());
  }
}

/** A file name a person can tell apart from the same side of another card. */
const exportFileName = (cardName: string, side: string): string => {
  const slug = cardName
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '');

  return `${slug || 'rate_card'}_${side}_export.csv`;
};

/**
 * Streams one side's published lines as CSV. A 409 names the columns the engine version no longer
 * produces, or says the card predates the parity axis, so that sentence is surfaced as written.
 */
export function* exportLinesRequest({ payload }: ExportLinesRequestAction): Generator<any, void, any> {
  try {
    // No errorType: this saga raises its own toast below, and one failure must not raise two.
    const result = yield call(sendRequest, exportLines, { id: payload.id, side: payload.side });
    const { fileBlobUrl, fileName } = result?.data ?? {};

    if (fileBlobUrl) {
      // The fallback is the name that actually gets used: `Content-Disposition` is not CORS-exposed, so
      // `fileName` is always null. Three cards from one engine version would otherwise share a file name.
      triggerDownload(fileBlobUrl, fileName || exportFileName(payload.cardName, payload.side));

      return;
    }

    yield put(
      notifications.requestError({
        error: result?.message || t('dash.admin.rateCards.view.errors.export_failed'),
      }),
    );
  } catch (error) {
    logger.error(error);
    yield put(notifications.requestError({ error: t('dash.admin.rateCards.view.errors.export_failed') }));
  } finally {
    yield put(actions.exportLinesSettled({ side: payload.side }));
  }
}

/**
 * Draft cards only, which the button already gates on. The request and its refusals are the list's
 * question too and are asked in one place; only `deleted`, which navigates, is this page's.
 */
export function* deleteCardRequest({ payload }: DeleteRequestAction): Generator<any, void, any> {
  const deleted = yield call(requestCardDelete, payload.id);

  yield put(deleted ? actions.deleteSuccess() : actions.deleteError());
}

export default function* rootSaga() {
  yield all([
    takeLatest(actionTypes.LOAD_CARD_REQUEST, loadCardRequest),
    takeLatest(actionTypes.POLL_START, pollCardRequest),
    takeLatest(actionTypes.LOAD_LINE_COUNT_REQUEST, loadLineCountRequest),
    takeLatest(actionTypes.LOAD_LINE_HEADERS_REQUEST, loadLineHeadersRequest),
    takeLatest(actionTypes.LOAD_LINES_REQUEST, loadLinesRequest),
    // `takeEvery`: exporting pay and bill are two different files, neither worth cancelling.
    takeEvery(actionTypes.EXPORT_LINES_REQUEST, exportLinesRequest),
    // Not what stops a double-click — the button's `deleting` guard does that, and this cancels the task,
    // not the request already sent. It is what keeps a superseded answer from landing after a newer one.
    takeLatest(actionTypes.DELETE_REQUEST, deleteCardRequest),
  ]);
}
